/**
 * F09 v2 — client-side form checks mirroring the frozen Zod bounds in
 * `@bravemove/types` (so the admin sees a field error before any request).
 */
import type { Translate } from './labels';

/** Length check for a text field; `required=false` allows an empty value. */
export function lengthError(
  t: Translate,
  value: string,
  min: number,
  max: number,
  required: boolean,
): string | undefined {
  const v = value.trim();
  if (v.length === 0) return required ? t('admin:verification.validation.required') : undefined;
  if (v.length < min) return t('admin:verification.validation.minChars', { min });
  if (v.length > max) return t('admin:verification.validation.maxChars', { max });
  return undefined;
}

/** Whole-number range check; `required=false` allows an empty value. */
export function intRangeError(
  t: Translate,
  raw: string,
  min: number,
  max: number,
  required: boolean,
): string | undefined {
  const v = raw.trim();
  if (v.length === 0) return required ? t('admin:verification.validation.required') : undefined;
  const n = Number(v);
  if (!Number.isInteger(n) || n < min || n > max) {
    return t('admin:verification.validation.range', { min, max });
  }
  return undefined;
}

/** True when every value in the record is undefined (no validation errors). */
export function noErrors(errors: Readonly<Record<string, string | undefined>>): boolean {
  return Object.values(errors).every((e) => e === undefined);
}
